import { useEffect, useState, useCallback } from "react";
import {
  Container,
  Typography,
  Box,
  Grid,
  Paper,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  LinearProgress,
  Stack,
  Divider,
} from "@mui/material";
import VerifiedIcon from "@mui/icons-material/Verified";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import DescriptionIcon from "@mui/icons-material/Description";
import GroupIcon from "@mui/icons-material/Group";
import { ServerConfig } from "../../../connectors/Config";

/**
 * DashboardConformita
 * -------------------------------------------------------------------
 * Cruscotto di conformità al Decreto Ministeriale D.Dir. 1986/2024.
 * Mostra:
 *  - KPI corsi conformi / non conformi (% conformità)
 *  - Stato documenti dei partecipanti
 *  - Attestati rilasciati
 *  - Tipologie discenti previste dal decreto
 *  - Dettaglio conformità per centro
 *
 * Sorgente dati: Lambda serverless2-dev-decretoManage
 *   GET {API_BASE_URL_2}/decreto/dashboard/conformita
 *   GET {API_BASE_URL_2}/decreto/tipologie
 */

const API = ServerConfig.AWS.Serverless.API_BASE_URL_2;

const KpiCard = ({ icon, label, value, sub, color }) => (
  <Card elevation={3} sx={{ borderRadius: 3, height: "100%" }}>
    <CardContent>
      <Stack direction="row" spacing={2} alignItems="center">
        <Box
          sx={{
            width: 52,
            height: 52,
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: color || "rgba(4,14,99,0.1)",
            color: "rgb(4,14,99)",
          }}
        >
          {icon}
        </Box>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: "bold", lineHeight: 1 }}>
            {value}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {label}
          </Typography>
          {sub != null && (
            <Typography variant="caption" color="text.secondary">
              {sub}
            </Typography>
          )}
        </Box>
      </Stack>
    </CardContent>
  </Card>
);

const DashboardConformita = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState(null);
  const [tipologie, setTipologie] = useState([]);
  const [scadenze, setScadenze] = useState({ adempimenti: [], totale: 0, scaduti: 0, urgenti: 0 });

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [dashRes, tipRes, scadRes] = await Promise.all([
        fetch(`${API}/decreto/dashboard/conformita`),
        fetch(`${API}/decreto/tipologie`),
        fetch(`${API}/decreto/scadenzario?giorni=60`),
      ]);
      if (!dashRes.ok) throw new Error("Errore nel caricamento delle statistiche");
      const dash = await dashRes.json();
      const tip = tipRes.ok ? await tipRes.json() : { tipologie: [] };
      const scad = scadRes.ok ? await scadRes.json() : { adempimenti: [], totale: 0, scaduti: 0, urgenti: 0 };
      setStats(dash);
      setTipologie(tip.tipologie || []);
      setScadenze(scad);
    } catch (e) {
      setError(e.message || "Errore imprevisto");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  if (loading) {
    return (
      <Container sx={{ py: 6, textAlign: "center" }}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>Caricamento cruscotto conformità…</Typography>
      </Container>
    );
  }

  if (error) {
    return (
      <Container sx={{ py: 6 }}>
        <Paper sx={{ p: 3, borderLeft: "6px solid #d32f2f" }}>
          <Typography color="error" variant="h6">
            Impossibile caricare il cruscotto
          </Typography>
          <Typography color="text.secondary">{error}</Typography>
        </Paper>
      </Container>
    );
  }

  const corsi = stats?.corsi || { totali: 0, conformi: 0, non_conformi: 0, perc: 0 };
  const part = stats?.partecipanti || {
    totali: 0,
    documenti_completi: 0,
    documenti_incompleti: 0,
  };
  const att = stats?.attestati || { rilasciati: 0 };
  const centri = stats?.dettaglio_centri || [];

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: "bold", color: "rgb(4,14,99)" }}>
          Conformità Decreto
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Cruscotto di monitoraggio della conformità al Decreto Ministeriale
          D.Dir. 1986/2024 (STCW / Maritime Security)
        </Typography>
      </Box>

      {/* KPI */}
      <Grid container spacing={3} sx={{ mb: 1 }}>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard
            icon={<VerifiedIcon />}
            value={`${corsi.perc}%`}
            label="Conformità corsi"
            sub={`${corsi.conformi}/${corsi.totali} corsi conformi`}
            color="rgba(46,125,50,0.12)"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard
            icon={<WarningAmberIcon />}
            value={corsi.non_conformi}
            label="Corsi da verificare"
            sub="Non ancora conformi"
            color="rgba(237,108,2,0.14)"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard
            icon={<GroupIcon />}
            value={part.documenti_incompleti}
            label="Discenti doc. incompleti"
            sub={`${part.documenti_completi}/${part.totali} completi`}
            color="rgba(4,14,99,0.10)"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard
            icon={<DescriptionIcon />}
            value={att.rilasciati}
            label="Attestati rilasciati"
            sub="Registro unico decreto"
            color="rgba(2,136,209,0.14)"
          />
        </Grid>
      </Grid>

      {/* Progress conformità globale */}
      <Paper sx={{ p: 3, borderRadius: 3, my: 3 }} elevation={2}>
        <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 1 }}>
          Livello di conformità complessivo
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Box sx={{ flexGrow: 1 }}>
            <LinearProgress
              variant="determinate"
              value={corsi.perc}
              sx={{ height: 14, borderRadius: 7 }}
              color={corsi.perc >= 80 ? "success" : corsi.perc >= 40 ? "warning" : "error"}
            />
          </Box>
          <Typography sx={{ fontWeight: "bold", minWidth: 48 }}>{corsi.perc}%</Typography>
        </Box>
        <Typography variant="caption" color="text.secondary">
          Un corso è conforme quando rispetta le durate minime di teoria/pratica
          previste dal decreto e ha i docenti assegnati.
        </Typography>
      </Paper>

      {/* Scadenzario adempimenti */}
      <Paper sx={{ p: 3, borderRadius: 3, my: 3 }} elevation={2}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
          <WarningAmberIcon color={scadenze.scaduti > 0 ? "error" : "warning"} />
          <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
            Scadenzario adempimenti (prossimi 60 giorni)
          </Typography>
          <Box sx={{ flexGrow: 1 }} />
          {scadenze.scaduti > 0 && (
            <Chip size="small" color="error" label={`${scadenze.scaduti} scaduti`} />
          )}
          {scadenze.urgenti > 0 && (
            <Chip size="small" color="warning" label={`${scadenze.urgenti} urgenti`} />
          )}
          <Chip size="small" label={`${scadenze.totale} totali`} />
        </Stack>
        <Divider sx={{ mb: 1 }} />
        {scadenze.adempimenti.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            Nessun adempimento in scadenza. Le comunicazioni obbligatorie (apertura corso
            H-15gg, chiusura H+30gg, scadenze documenti) verranno elencate qui.
          </Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Adempimento</TableCell>
                <TableCell>Corso</TableCell>
                <TableCell>Scadenza</TableCell>
                <TableCell align="center">Giorni residui</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {scadenze.adempimenti.slice(0, 15).map((a) => (
                <TableRow key={a.id}>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {a.tipo_adempimento}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {a.descrizione}
                    </Typography>
                  </TableCell>
                  <TableCell>{a.acronimo || "—"}</TableCell>
                  <TableCell>{a.data_scadenza}</TableCell>
                  <TableCell align="center">
                    <Chip
                      size="small"
                      label={a.giorni_residui < 0 ? `${Math.abs(a.giorni_residui)}gg fa` : `${a.giorni_residui}gg`}
                      color={a.giorni_residui < 0 ? "error" : a.giorni_residui <= 7 ? "warning" : "default"}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Paper>

      <Grid container spacing={3}>
        {/* Dettaglio per centro */}
        <Grid item xs={12} md={7}>
          <Paper sx={{ p: 2, borderRadius: 3, height: "100%" }} elevation={2}>
            <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 1 }}>
              Conformità per centro
            </Typography>
            <Divider sx={{ mb: 1 }} />
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Centro (ID)</TableCell>
                  <TableCell align="center">Corsi</TableCell>
                  <TableCell align="center">Conformi</TableCell>
                  <TableCell align="center">%</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {centri.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      Nessun dato disponibile
                    </TableCell>
                  </TableRow>
                )}
                {centri.map((c) => {
                  const p = c.corsi > 0 ? Math.round((c.conformi / c.corsi) * 100) : 0;
                  return (
                    <TableRow key={c.centro}>
                      <TableCell>
                        <Typography variant="caption" sx={{ fontFamily: "monospace" }}>
                          {c.centro}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">{c.corsi}</TableCell>
                      <TableCell align="center">{c.conformi}</TableCell>
                      <TableCell align="center">
                        <Chip
                          size="small"
                          label={`${p}%`}
                          color={p >= 80 ? "success" : p >= 40 ? "warning" : "default"}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Paper>
        </Grid>

        {/* Tipologie discenti */}
        <Grid item xs={12} md={5}>
          <Paper sx={{ p: 2, borderRadius: 3, height: "100%" }} elevation={2}>
            <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 1 }}>
              Tipologie discenti (decreto)
            </Typography>
            <Divider sx={{ mb: 1 }} />
            <Stack spacing={1}>
              {tipologie.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                  Nessuna tipologia configurata
                </Typography>
              )}
              {tipologie.map((t) => (
                <Box key={t.id} sx={{ p: 1, borderRadius: 2, bgcolor: "rgba(4,14,99,0.04)" }}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Chip size="small" label={t.codice} color="primary" />
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {t.descrizione}
                    </Typography>
                  </Stack>
                  {t.descrizione_completa && (
                    <Typography variant="caption" color="text.secondary">
                      {t.descrizione_completa}
                    </Typography>
                  )}
                </Box>
              ))}
            </Stack>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default DashboardConformita;
